import { useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  filterStoreRepo,
  ValueFilter,
  ValueFilterState,
} from 'components/Filters/FilterStore'
import { useMountEffect } from 'hooks/useMountEffect'
import { COMPANY_CATEGORIES_QUERY_KEY } from 'services/api'
import type { MobileSearchFilters } from './useMobileSearch'

const FILTER_KEYS = ['category', 'type'] as const

type FilterKey = (typeof FILTER_KEYS)[number]

/** Resolve a category / type slug to its human label (falls back to the slug). */
export type ResolveFilterLabel = (key: FilterKey, slug: string) => string

/** category / type slug string ("a,b") -> ValueFilter[] for the store. */
function toValueFilters(
  filters: MobileSearchFilters,
  resolveLabel?: ResolveFilterLabel
): {
  category?: ValueFilter[]
  type?: ValueFilter[]
} {
  const result: { category?: ValueFilter[]; type?: ValueFilter[] } = {}
  FILTER_KEYS.forEach((key) => {
    const slugs = filters[key]?.split(',').filter(Boolean) ?? []
    if (slugs.length) {
      // Resolve labels from the (app-prefetched) categories data so chips
      // seeded from the URL read "Fast Casual" rather than the raw slug. Falls
      // back to the slug when the data isn't loaded yet.
      result[key] = slugs.map((slug) => ({
        value: slug,
        label: resolveLabel?.(key, slug) ?? slug,
      }))
    }
  })
  return result
}

/**
 * Upgrade placeholder chip labels (label still equal to the slug) to their
 * resolved display name once the categories data is available. Only ever
 * *upgrades* — never downgrades a real label back to a slug — so it's safe to
 * run repeatedly as the async fetch settles. Returns `null` when nothing
 * changed, so callers can skip a no-op store write.
 */
function relabel(
  valueFilters: ValueFilterState,
  resolveLabel: ResolveFilterLabel
): ValueFilterState | null {
  let changed = false
  const next: ValueFilterState = { ...valueFilters }
  FILTER_KEYS.forEach((key) => {
    const current = valueFilters[key]
    if (!current?.length) return
    let keyChanged = false
    const mapped = current.map((vf) => {
      const resolved = resolveLabel(key, vf.value)
      // Skip when unresolved (falls back to the slug) or already correct.
      if (resolved === vf.value || resolved === vf.label) return vf
      keyChanged = true
      return { ...vf, label: resolved }
    })
    if (keyChanged) {
      next[key] = mapped
      changed = true
    }
  })
  return changed ? next : null
}

/** Applied store value-filters -> category / type slug strings. */
function toSearchFilters(valueFilters: {
  category?: ValueFilter[]
  type?: ValueFilter[]
}): MobileSearchFilters {
  const join = (values?: ValueFilter[]) => {
    const slugs = (values ?? []).map((v) => v.value).filter(Boolean)
    return slugs.length ? slugs.join(',') : undefined
  }
  return {
    category: join(valueFilters.category),
    type: join(valueFilters.type),
  }
}

export function useMobileSearchFilterStore(
  identifier: 'MOBILE_COMPANY_SEARCH',
  initialFilters: MobileSearchFilters,
  onFiltersChange: (filters: MobileSearchFilters) => void,
  resolveLabel?: ResolveFilterLabel
) {
  const queryClient = useQueryClient()

  // disablePersist: this store is uniquely double-persisted — the URL (via
  // useMobileSearch) is the source of truth, so localStorage rehydration here
  // would make the mount-time seed guard below skip reseeding from the URL,
  // leaving FilterChips showing a stale category the actual query no longer has.
  // Keep persistence off so the URL alone drives the store. Both call sites must
  // pass true — getStore only honors the flag when it first creates the store.
  const filterStore = useMemo(
    () => filterStoreRepo.getStore(identifier, /* disablePersist */ true),
    [identifier]
  )

  // Keep the latest change handler / resolver in refs so the mount-time
  // subscriptions always call the current closures (avoids stale captures).
  const onFiltersChangeRef = useRef(onFiltersChange)
  onFiltersChangeRef.current = onFiltersChange
  const resolveLabelRef = useRef(resolveLabel)
  resolveLabelRef.current = resolveLabel

  useMountEffect(() => {
    // Seed the store from the current search filters so the modal + chips
    // reflect what's already active without overwriting a hydrated store.
    const seeded = toValueFilters(initialFilters, resolveLabel)
    const { valueFilters } = filterStore.getState()
    if (!valueFilters?.category?.length && !valueFilters?.type?.length) {
      filterStore.setState({
        valueFilters: seeded,
        pendingValueFilters: seeded,
      })
    }

    // Chips seeded from the URL start with the slug as their label when the
    // categories fetch hasn't resolved yet (the common case on a cold deep
    // link). Re-resolve those placeholder labels once the categories data
    // lands so the chip flips from "fast-casual" to "Fast Casual", instead of
    // staying a raw slug for the session.
    const backfillLabels = () => {
      const resolve = resolveLabelRef.current
      if (!resolve) return
      const state = filterStore.getState()
      const nextApplied = relabel(state.valueFilters, resolve)
      const nextPending = relabel(state.pendingValueFilters, resolve)
      if (nextApplied || nextPending) {
        filterStore.setState({
          valueFilters: nextApplied ?? state.valueFilters,
          pendingValueFilters: nextPending ?? state.pendingValueFilters,
        })
      }
    }
    backfillLabels()
    // Only react to the company-categories query settling — the resolver
    // depends solely on that reference data, so ignore unrelated cache events.
    const unsubscribeCache = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] === COMPANY_CATEGORIES_QUERY_KEY[0]) {
        backfillLabels()
      }
    })

    // Keep the search state in sync with applied category / type filters.
    let previous = toSearchFilters(filterStore.getState().valueFilters)
    const unsubscribeStore = filterStore.subscribe((state) => {
      const next = toSearchFilters(state.valueFilters)
      if (next.category !== previous.category || next.type !== previous.type) {
        previous = next
        onFiltersChangeRef.current(next)
      }
    })

    return () => {
      unsubscribeCache()
      unsubscribeStore()
    }
  })

  return filterStore
}
